import { Alert, Box, Typography } from '@mui/material';
import React from 'react';
import type { RegistryState } from '../data/types';
import { buildAmmaStatement } from '../domain/taxOutputs';
import { date, money } from '../lib/format';
import DocumentPaper, { DocTable } from './DocumentPaper';

interface Props {
  state: RegistryState;
  holderId: string;
  financialYear: string;
  fundId?: string;
  issued: string;
}

export default function AmmaStatementView({
  state,
  holderId,
  financialYear,
  fundId,
  issued,
}: Props) {
  const statement = buildAmmaStatement(state, holderId, financialYear, fundId);
  if (!statement) return <Alert severity="info">No statement for this holder.</Alert>;
  const holder = statement.holder;
  const tenant = state.tenants.find((item) => item.id === holder.tenantId)!;
  const code = (id: string) => state.funds.find((fund) => fund.id === id)?.code ?? id;
  const t = statement.totals;
  const fy = financialYear.replace('FY', '');
  return (
    <DocumentPaper
      tenant={tenant}
      holder={holder}
      issued={issued}
      title={`AMMA tax statement ${Number(fy) - 1}–${fy.slice(2)}`}
      subtitle={`Attribution managed investment trust member annual statement${fundId ? ` · ${state.funds.find((fund) => fund.id === fundId)?.name}` : ' · all trusts'}`}
      footnote="Use this statement to complete your tax return. Tax return labels are shown for an individual and are indicative."
    >
      {statement.archived && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Issued from distribution history migrated from the previous registry.
        </Alert>
      )}
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Part A · Summary for your tax return
      </Typography>
      <DocTable
        head={['Item', 'Tax return label', 'Amount']}
        rows={[
          [
            'Share of net income from trusts (less capital gains and foreign income)',
            '13U',
            money(t.assessable, true),
          ],
          ['Credit for TFN amounts withheld', '13R', money(t.tfnWithheld, true)],
          ['Total current year capital gains (grossed up)', '18H', money(t.grossedUpGain, true)],
          [
            'Net capital gain (if you have no capital losses, after the 50% discount)',
            '18A',
            money(t.discountedGainCash, true),
          ],
          ['Assessable foreign source income', '20E / 20M', money(t.foreignIncome, true)],
          [
            'AMIT cost base net amount — excess (reduces cost base)',
            'n/a',
            money(t.taxDeferred, true),
          ],
        ]}
      />
      <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
        Part B · Components of attribution
      </Typography>
      <DocTable
        head={['Component', 'Cash distribution', 'Attributed amount']}
        rows={[
          ['Interest', money(t.interest, true), money(t.interest, true)],
          ['Other Australian income', money(t.otherIncome, true), money(t.otherIncome, true)],
          [
            'Discounted capital gain',
            money(t.discountedGainCash, true),
            money(t.grossedUpGain, true),
          ],
          ['Foreign income', money(t.foreignIncome, true), money(t.foreignIncome, true)],
          ['Tax-deferred (AMIT cost base adjustment)', money(t.taxDeferred, true), '—'],
        ]}
        total={['Gross distributions', money(t.gross, true), '']}
      />
      <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
        Part C · Distributions in the year
      </Typography>
      <DocTable
        head={['Trust and period', 'Paid', 'Gross', 'Withheld', 'Net']}
        rows={statement.lines.map((line) => [
          `${code(line.fundId)} · ${line.label}`,
          date(line.paymentDate),
          money(line.gross, true),
          money(line.tfnWithheld + line.nrWithheld, true),
          money(line.gross - line.tfnWithheld - line.nrWithheld, true),
        ])}
        total={[
          'Total',
          '',
          money(t.gross, true),
          money(t.tfnWithheld + t.nrWithheld, true),
          money(t.gross - t.tfnWithheld - t.nrWithheld, true),
        ]}
      />
      {t.nrWithheld > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2">
            Non-resident withholding of {money(t.nrWithheld, true)} was deducted and remitted to the
            ATO.
          </Typography>
        </Box>
      )}
    </DocumentPaper>
  );
}
